const nameToNumber = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

const objectHashCache = new WeakMap();
let hashCounter = 0;

/**
 * Creates a stable hash for any JavaScript value. Supports primitives, objects, arrays,
 * BigInt, NaN, Symbol, functions, classes. Sorts object keys and handles circular references.
 *
 * @param {*} value - Any JavaScript value to hash
 * @returns {string} Stable hash string
 *
 * @example
 * consistentHash(42)                    // "42"
 * consistentHash({ b: 2, a: 1 })        // "#a:1,b:2" (keys sorted)
 * consistentHash([1, 2, 3])             // "@1,2,3"
 * consistentHash(BigInt(123))           // "123n"
 * consistentHash(Symbol('id'))          // "Symbol(id)"
 *
 * // Circular references are safe
 * const obj = { name: 'test' };
 * obj.self = obj;
 * consistentHash(obj);                  // "1~"
 */
export function consistentHash(value) {
  const type = typeof value;
  const constructor = value?.constructor;

  if (Object(value) === value && constructor !== Date && constructor !== RegExp) {
    let hash = objectHashCache.get(value);
    if (hash) return hash;

    // eslint-disable-next-line no-plusplus
    hash = `${++hashCounter}~`;
    objectHashCache.set(value, hash);

    if (Array.isArray(value)) {
      hash = `@${value.map((item) => consistentHash(item)).join(',')}`;
      objectHashCache.set(value, hash);
    } else if (constructor === Object) {
      const sortedKeys = Object.keys(value).sort().reverse();
      const pairs = sortedKeys
        .filter((key) => value[key] !== undefined)
        .map((key) => `${key}:${consistentHash(value[key])}`);

      hash = `#${pairs.join(',')}`;
      objectHashCache.set(value, hash);
    }

    return `#${nameToNumber(hash)}`;
  }

  if (constructor === Date) return value.toJSON();
  if (type === 'symbol') return value.toString();
  if (type === 'string') return JSON.stringify(value);

  return String(value);
}
